import { Link } from "react-router-dom";
import { format, isToday } from "date-fns";
import { it } from "date-fns/locale";
import { InstallationSchedule } from "../../types";
import { Badge } from "./Badge";

/** Raggruppa le installazioni per data, con "Oggi" al posto della data quando
 *  coincide — stessa lettura a colpo d'occhio della pagina Installazioni. */
function groupByDay(records: InstallationSchedule[]): { label: string; isToday: boolean; items: InstallationSchedule[] }[] {
  const groups = new Map<string, InstallationSchedule[]>();
  for (const record of records) {
    if (!record.ipotesi_data) continue;
    const key = record.ipotesi_data.slice(0, 10);
    const list = groups.get(key) ?? [];
    list.push(record);
    groups.set(key, list);
  }
  return Array.from(groups.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, items]) => {
      const date = new Date(key);
      return { label: isToday(date) ? "Oggi" : format(date, "EEE d MMM", { locale: it }), isToday: isToday(date), items };
    });
}

interface Prossimi7GiorniProps {
  records: InstallationSchedule[];
  isLoading: boolean;
  /** Se false le righe non sono cliccabili: serve al ruolo cliente, che non ha
   *  accesso alla pagina Installazioni (/schedule). */
  linkToInstallazioni?: boolean;
}

/** Elenco delle installazioni previste nei prossimi 7 giorni, raggruppate per
 *  giorno. Componente condiviso tra Dashboard (ruoli interni) e Calendario
 *  unificato (ruolo cliente). */
export function Prossimi7Giorni({ records, isLoading, linkToInstallazioni = true }: Prossimi7GiorniProps) {
  return (
    <section>
      <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-ink/50">Prossimi 7 giorni</h2>

      {isLoading && <p className="text-sm text-ink/40">Caricamento…</p>}

      {!isLoading && records.length === 0 && (
        <div className="rounded-card border border-dashed border-line bg-white/50 p-6 text-center text-sm text-ink/50">
          Nessuna installazione prevista nei prossimi 7 giorni.
        </div>
      )}

      {!isLoading && records.length > 0 && (
        <div className="overflow-hidden rounded-card border border-line bg-white shadow-card">
          {groupByDay(records).map((group) => (
            <div key={group.label}>
              <p
                className={`border-t border-line px-4 pt-3 text-xs font-bold uppercase tracking-wide first:border-t-0 ${
                  group.isToday ? "text-accent-dark" : "text-ink/40"
                }`}
              >
                {group.label}
              </p>
              {group.items.map((record) => {
                const riga = (
                  <>
                    <div className="min-w-0">
                      <p className="truncate font-medium text-ink">{record.insegna ?? "Senza insegna"}</p>
                      <p className="truncate text-xs text-ink/50">{record.localita ?? "—"}</p>
                    </div>
                    <Badge stato={record.stato}>{record.stato}</Badge>
                  </>
                );
                const cls = "flex items-center justify-between gap-3 px-4 py-3 text-sm";
                return linkToInstallazioni ? (
                  <Link key={record.id} to="/schedule" className={`${cls} transition-colors duration-150 hover:bg-primary/5`}>
                    {riga}
                  </Link>
                ) : (
                  <div key={record.id} className={cls}>
                    {riga}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
